import { axiosInstance } from './baseApi';

const procoreApi = {
  // ========== OAUTH HELPERS ==========

  /**
   * Generate the Procore OAuth authorization URL
   */
  getOAuthUrl: (token, clientId, redirectUri = 'urn:ietf:wg:oauth:2.0:oob', useSandbox = true) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetOAuthUrl`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: {
        clientId,
        redirectUri,
        useSandbox,
      },
    }),

  /**
   * Exchange an authorization code for access and refresh tokens
   */
  exchangeCodeForTokens: (token, code, clientId, clientSecret, redirectUri = 'urn:ietf:wg:oauth:2.0:oob', useSandbox = true, accountId = null) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/ExchangeCodeForTokens`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: {
        code,
        clientId,
        clientSecret,
        redirectUri,
        useSandbox,
        accountId,
      },
    }),

  /**
   * Refresh an access token using a refresh token
   */
  refreshAccessToken: (token, refreshToken, clientId, clientSecret, useSandbox = true, accountId = null) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/RefreshAccessToken`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: {
        refreshToken,
        clientId,
        clientSecret,
        useSandbox,
        accountId,
      },
    }),

  // ========== MANPOWER DATA PUSH ==========

  /**
   * Get manpower preview data from CloudLoq for a specific date
   */
  getManpowerPreview: (token, { accountId, siteId, date }) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetManpowerPreview`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: {
        accountId,
        siteId,
        date,
      },
    }),

  pushManpowerToProcore: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/PushManpowerToProcore`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  // ========== VISITOR DATA PUSH ==========

  /**
   * Get visitor preview data from CloudLoq for a specific date (FirstInLastOut with event history)
   */
  getVisitorPreview: (token, { accountId, siteId, date }) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetVisitorPreview`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: {
        accountId,
        siteId,
        date,
      },
    }),

  pushVisitorsToProcore: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/PushVisitorsToProcore`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  // ========== MANPOWER APIs ==========

  /**
   * Get manpower logs for a specific date
   */
  getManpowerLogs: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetManpowerLogs`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Get vendor options for manpower logs
   */
  getManpowerVendorOptions: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetManpowerVendorOptions`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  // ========== VISITOR APIs ==========

  /**
   * Get visitor logs for a specific date
   */
  getVisitorLogs: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetVisitorLogs`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Get contact options for visitor logs
   */
  getVisitorContactOptions: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetVisitorContactOptions`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Get people in the project
   */
  getPeople: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetPeople`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: { employeesOnly: false, ...data },
    }),

  // ========== REFERENCE DATA APIs ==========

  /**
   * Get all locations in the project
   */
  getLocations: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetLocations`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Get all employees in the company
   */
  getEmployees: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetEmployees`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Get an employee by ID
   */
  getEmployeeById: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetEmployeeById`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Get an employee by email
   */
  getEmployeeByEmail: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetEmployeeByEmail`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Get all vendor companies
   */
  getVendorCompanies: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetVendorCompanies`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Get a vendor company by ID
   */
  getVendorCompanyById: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetVendorCompanyById`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Get personnel for a vendor
   */
  getVendorPersonnel: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetVendorPersonnel`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  // ========== DAILY PUSH SCHEDULE ==========

  updateDailyPushSchedule: (token, accountId, dailyPushHourUtc) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/UpdateDailyPushSchedule`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: {
        accountId,
        dailyPushHourUtc,
      },
    }),

  // ========== CONNECTION & SITE MAPPING ==========

  /**
   * Get the Procore connection config and site mappings for the account
   */
  getConnection: (token, accountId) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetConnection`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: {
        accountId,
      },
    }),

  /**
   * Save or update the Procore connection for an account
   */
  saveConnection: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/SaveConnection`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Update tokens after OAuth exchange or refresh
   */
  updateTokens: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/UpdateTokens`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Save or update a Procore site mapping
   */
  saveSiteMapping: (token, data) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/SaveSiteMapping`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data,
    }),

  /**
   * Delete a Procore site mapping
   */
  deleteSiteMapping: (token, accountId, siteId) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/DeleteSiteMapping`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: {
        accountId,
        siteId,
      },
    }),

  /**
   * Get the site mapping for a specific site
   */
  getSiteMapping: (token, accountId, siteId) =>
    axiosInstance({
      method: 'post',
      url: `${process.env.REACT_APP_CLOUD_API_URL}/Procore/GetSiteMapping`,
      headers: {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token,
      },
      data: {
        accountId,
        siteId,
      },
    }),
};

export default procoreApi;
